import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { SpecraIconButton } from '../ensigns/icon-button';
/**
 * Slides one at a time: a source page, an image, a chart. Previous / Next
 * move between them; every slide stays in the DOM so its
 * content is searchable and printable.
 */ export function SpecraCarousel({ slides, label, title, className, ...props }) {
    const [index, setIndex] = React.useState(0);
    const count = slides.length;
    const go = React.useCallback((next)=>setIndex((next % count + count) % count), [
        count
    ]);
    if (count === 0) return null;
    const current = slides[Math.min(index, count - 1)];
    return /*#__PURE__*/ _jsxs("section", {
        "aria-roledescription": "carousel",
        "aria-label": label,
        className: cn('rounded-md border border-border bg-card', className),
        ...props,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 border-b border-border px-3 py-2",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "min-w-0 flex-1 truncate text-[length:var(--fs-ui-caption)] font-medium text-foreground",
                        children: title ?? label
                    }),
                    count > 1 && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-[length:var(--fs-ui-caption)] tabular-nums text-muted-foreground",
                                children: [
                                    index + 1,
                                    " of ",
                                    count
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraIconButton, {
                                label: "Previous",
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>go(index - 1),
                                children: /*#__PURE__*/ _jsx(ChevronLeft, {
                                    className: "h-4 w-4",
                                    "aria-hidden": true
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraIconButton, {
                                label: "Next",
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>go(index + 1),
                                children: /*#__PURE__*/ _jsx(ChevronRight, {
                                    className: "h-4 w-4",
                                    "aria-hidden": true
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "p-3",
                children: slides.map((s)=>/*#__PURE__*/ _jsxs("section", {
                        "aria-roledescription": "slide",
                        "aria-label": s.label ?? `${slides.indexOf(s) + 1} of ${count}`,
                        hidden: s.id !== current.id,
                        children: [
                            s.content,
                            s.label && /*#__PURE__*/ _jsx("div", {
                                className: "mt-2 text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                children: s.label
                            })
                        ]
                    }, s.id))
            }),
            count > 1 && /*#__PURE__*/ _jsx("div", {
                className: "flex justify-center gap-1.5 pb-2.5",
                "aria-hidden": true,
                children: slides.map((s)=>/*#__PURE__*/ _jsx("span", {
                        className: cn('h-1.5 w-1.5 rounded-full', s.id === current.id ? 'bg-foreground' : 'bg-muted-foreground/30')
                    }, s.id))
            })
        ]
    });
}
